import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Search, X } from 'lucide-react';
import { listPlaygrounds, deletePlayground } from 'features/playground/api/playgroundApi';
import { useBreadcrumb } from 'shared/context/BreadcrumbContext';
import { useAppDialog } from 'shared/context/AppDialogContext';
import BrandLoader from 'shared/components/ui/BrandLoader';
import BulkBar from 'shared/components/tables/BulkBar';
import { usePermissions, PERMISSIONS } from 'shared/hooks/usePermissions';
import { primaryButtonClass } from 'features/playground/styles';
import {
  activitySearchWrapperClass,
  activitySearchInputClass,
  tableHeaderRowClass,
  tableHeaderCellClass,
  tableRowClass,
  tableDataCellClass,
} from 'features/activity/styles/dataTableStyles';

function timeAgo(dateStr) {
  if (!dateStr) return '—';
  const secs = Math.floor((Date.now() - new Date(dateStr)) / 1000);
  if (secs < 60) return `${secs}s ago`;
  const mins = Math.floor(secs / 60);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d ago`;
  const weeks = Math.floor(days / 7);
  if (weeks < 5) return `${weeks}w ago`;
  const months = Math.floor(days / 30);
  if (months < 12) return `${months}mo ago`;
  return `${Math.floor(days / 365)}y ago`;
}

export default function PlaygroundListPage() {
  useBreadcrumb([{ label: 'Playground' }]);

  const { confirm: appConfirm } = useAppDialog();
  const { team } = useParams();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [query, setQuery] = useState('');
  const canWrite = usePermissions().can(PERMISSIONS.APP_USE);
  const [picked, setPicked] = useState(() => new Set());
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setItems(await listPlaygrounds());
    } catch (e) {
      setError(e?.response?.data?.error || e.message || 'Failed to load playgrounds');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return items;
    return items.filter((p) => (p.name || '').toLowerCase().includes(q));
  }, [items, query]);

  // A new playground is a draft until it is named and saved: nothing is
  // created here, so leaving the editor leaves nothing behind.
  const open = (pg) => navigate(`/${encodeURIComponent(team)}/playground/${pg.id}`);
  const handleCreate = () => navigate(`/${encodeURIComponent(team)}/playground/new`);

  // A reload (or a search) can drop rows the selection still holds.
  useEffect(() => { setPicked((cur) => new Set([...cur].filter((id) => items.some((r) => r.id === id)))); }, [items]);
  const chosen = items.filter((r) => picked.has(r.id));
  const allPicked = filtered.length > 0 && filtered.every((r) => picked.has(r.id));
  const toggleOne = (id) => setPicked((cur) => { const n = new Set(cur); if (n.has(id)) n.delete(id); else n.add(id); return n; });

  const removeChosen = async () => {
    if (!chosen.length) return;
    const many = chosen.length > 1;
    const ok = await appConfirm(
      many ? `${chosen.length} playgrounds will be removed.` : `"${chosen[0].name}" will be removed.`,
      { title: many ? 'Delete playgrounds' : 'Delete playground', confirmLabel: 'Delete', danger: true },
    );
    if (!ok) return;
    setBusy(true);
    const res = await Promise.allSettled(chosen.map((r) => deletePlayground(r.id)));
    const failed = res.filter((x) => x.status === 'rejected');
    setPicked(new Set());
    await refresh();
    if (failed.length) setError(failed[0].reason?.response?.data?.error || failed[0].reason?.message || 'Something failed');
    setBusy(false);
  };

  return (
    <main
      className="relative flex flex-1 min-h-0 flex-col overflow-hidden bg-surface"
      style={{ fontFamily: 'Inter, sans-serif' }}
    >
      {/* Toolbar */}
      <div className="flex shrink-0 flex-col gap-2 bg-surface px-3 sm:px-4 pb-2 pt-3">
        <div className="flex flex-wrap items-center gap-2">

        {/* Count chip */}
        <div className="inline-flex h-8 items-stretch overflow-hidden rounded-md border border-[color:var(--border-default)] bg-surface">
          <div className="flex items-center gap-1.5 px-3 text-[12px]">
            <span className="font-semibold tabular-nums text-[color:var(--text-primary)]">
              {loading ? '…' : filtered.length.toLocaleString()}
            </span>
            <span className="text-[color:var(--text-muted)]">
              {filtered.length === 1 ? 'playground' : 'playgrounds'}
            </span>
          </div>
        </div>

        {/* Search: ml-auto pushes right-side actions to the far right */}
        <div className={`${activitySearchWrapperClass} ml-auto w-[220px]`}>
          <Search size={13} className="flex-none text-[color:var(--text-muted)]" aria-hidden />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search playgrounds"
            className={activitySearchInputClass}
          />
          {query ? (
            <button
              type="button"
              onClick={() => setQuery('')}
              className="flex-none text-[color:var(--text-muted)] transition-colors hover:text-[color:var(--text-primary)]"
              aria-label="Clear search"
              title="Clear search"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          ) : null}
        </div>

        {/* New playground */}
        <button type="button" onClick={handleCreate} className={primaryButtonClass}>
          New playground
        </button>
        </div>
      </div>

      {error && (
        <p role="alert" className="px-4 pt-3 text-[12px] text-[color:var(--feedback-error)]">{error}</p>
      )}

      {/* Content */}
      <div className="min-h-0 flex-1 overflow-auto px-4 pb-8 pt-3">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <BrandLoader label="Loading playgrounds" size="md" />
          </div>
        ) : (
          <div className="overflow-hidden rounded-md border border-[color:var(--border-default)] bg-surface">
            <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead>
                <tr className={tableHeaderRowClass}>
                  <th scope="col" className={`${tableHeaderCellClass} w-8`}>
                    {canWrite && (
                      <input type="checkbox" checked={allPicked} aria-label={allPicked ? 'Clear the selection' : 'Select all'}
                        onChange={() => setPicked(allPicked ? new Set() : new Set(filtered.map((r) => r.id)))} />
                    )}
                  </th>
                  <th scope="col" className={tableHeaderCellClass}>Name</th>
                  <th scope="col" className={tableHeaderCellClass}>Last updated</th>
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 && (
                  <tr>
                    <td colSpan={3} className="px-3 py-10 text-center text-[12px] text-[color:var(--text-muted)]">
                      {error ? 'Could not load playgrounds' : query.trim() ? `No playgrounds match "${query.trim()}"` : 'No playgrounds yet'}
                    </td>
                  </tr>
                )}
                {filtered.map((pg) => (
                  <tr
                    key={pg.id}
                    className={`${tableRowClass} outline-none focus-visible:bg-surface-hover`}
                    tabIndex={0}
                    onClick={() => open(pg)}
                    onKeyDown={(e) => { if (e.key === 'Enter' && e.target === e.currentTarget) open(pg); }}
                  >
                    <td className={`${tableDataCellClass} w-8`} onClick={(e) => e.stopPropagation()}>
                      {canWrite && <input type="checkbox" checked={picked.has(pg.id)} aria-label={`Select ${pg.name}`} onChange={() => toggleOne(pg.id)} />}
                    </td>
                    <td className={tableDataCellClass}>
                      <span className="truncate font-medium">{pg.name}</span>
                    </td>
                    <td className={`${tableDataCellClass} text-[color:var(--text-muted)]`}>
                      {timeAgo(pg.updated_at)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          </div>
        )}
      </div>
      <BulkBar count={canWrite ? picked.size : 0} label="Selected playgrounds" onClear={() => setPicked(new Set())} actions={[
        { label: 'Delete', disabled: busy, onClick: removeChosen, danger: true },
      ]} />
    </main>
  );
}
